import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface CostCenter {
  id: string
  name: string
  code?: string
  description?: string
  parentId?: string
  parent?: {
    name: string
  }
  children?: CostCenter[]
  isActive: boolean
}

const CostCenters: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingCostCenter, setEditingCostCenter] = useState<CostCenter | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list')
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    parentId: '',
    isActive: true,
  })

  useEffect(() => {
    fetchCostCenters()
  }, [viewMode])

  const fetchCostCenters = async () => {
    try {
      setIsLoading(true)
      const endpoint = viewMode === 'tree' ? '/api/erp/cost-centers/tree' : '/api/erp/cost-centers'
      const response = await axios.get(endpoint)
      setCostCenters(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching cost centers:', error)
      toast.error('Failed to load cost centers')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.post('/api/erp/cost-centers', {
        name: formData.name,
        code: formData.code || undefined,
        description: formData.description || undefined,
        parentId: formData.parentId || undefined,
        isActive: formData.isActive,
      })
      toast.success('Cost center created successfully')
      setShowCreateModal(false)
      setFormData({
        name: '',
        code: '',
        description: '',
        parentId: '',
        isActive: true,
      })
      fetchCostCenters()
    } catch (error: any) {
      console.error('Error creating cost center:', error)
      toast.error(error.response?.data?.error || 'Failed to create cost center')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCostCenter) return

    if (!formData.name) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.put(`/api/erp/cost-centers/${editingCostCenter.id}`, {
        name: formData.name,
        code: formData.code || undefined,
        description: formData.description || undefined,
        parentId: formData.parentId || undefined,
        isActive: formData.isActive,
      })
      toast.success('Cost center updated successfully')
      setShowEditModal(false)
      setEditingCostCenter(null)
      fetchCostCenters()
    } catch (error: any) {
      console.error('Error updating cost center:', error)
      toast.error(error.response?.data?.error || 'Failed to update cost center')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this cost center? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/cost-centers/${id}`)
      toast.success('Cost center deleted successfully')
      fetchCostCenters()
    } catch (error: any) {
      console.error('Error deleting cost center:', error)
      toast.error(error.response?.data?.error || 'Failed to delete cost center')
    }
  }

  const renderCostCenterTree = (centers: CostCenter[], level: number = 0): JSX.Element[] => {
    return centers.map((center) => (
      <React.Fragment key={center.id}>
        <tr className={center.children && center.children.length > 0 ? 'bg-gray-50 font-semibold hover:bg-gray-100' : 'hover:bg-gray-50'}>
          <td className="px-8 py-5 border-b" style={{ paddingLeft: `${level * 40 + 32}px` }}>
            <div className="flex items-center space-x-3">
              {level > 0 && (
                <span className="text-gray-300">└</span>
              )}
              {center.children && center.children.length > 0 && (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
              <span className={center.children && center.children.length > 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}>{center.code || '-'}</span>
            </div>
          </td>
          <td className="px-8 py-5 border-b">
            <span className={center.children && center.children.length > 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}>{center.name}</span>
          </td>
          <td className="px-8 py-5 border-b text-gray-600">{center.parent?.name || '-'}</td>
          <td className="px-8 py-5 border-b text-gray-600">{center.description || '-'}</td>
          <td className="px-8 py-5 border-b">
            <span className={`px-2 py-1 text-xs rounded-full ${
              center.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {center.isActive ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td className="px-8 py-5 border-b">
            <div className="flex space-x-2">
              {hasPermission('erp_cost_center', 'write') && (
                <button
                  onClick={() => {
                    setEditingCostCenter(center)
                    setFormData({
                      name: center.name,
                      code: center.code || '',
                      description: center.description || '',
                      parentId: center.parentId || '',
                      isActive: center.isActive,
                    })
                    setShowEditModal(true)
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Edit
                </button>
              )}
              {hasPermission('erp_cost_center', 'delete') && (
                <button
                  onClick={() => handleDelete(center.id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              )}
            </div>
          </td>
        </tr>
        {center.children && center.children.length > 0 && renderCostCenterTree(center.children, level + 1)}
      </React.Fragment>
    ))
  }

  if (permissionsLoading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading permissions...</div>
    )
  }

  if (!hasPermission('erp_cost_center', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view cost centers.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Cost Centers</h1>
            <PageHelp pageIdentifier="cost-centers" title="Cost Centers" />
          </div>
          <p className="text-gray-600 mt-1">Manage cost centers for expense allocation</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={viewMode}
            onChange={(e) => {
              setViewMode(e.target.value as 'list' | 'tree')
              fetchCostCenters()
            }}
            className="border border-gray-300 rounded px-3 py-2 w-full min-w-[140px] text-sm"
          >
            <option value="tree">Tree View</option>
            <option value="list">List View</option>
          </select>
          {hasPermission('erp_cost_center', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Create Cost Center</span>
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : costCenters.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No cost centers found. Create your first cost center to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-8 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {viewMode === 'tree' ? (
                renderCostCenterTree(costCenters)
              ) : (
                costCenters.map((center) => (
                  <tr key={center.id} className="hover:bg-gray-50">
                    <td className="px-8 py-5 border-b">{center.code || '-'}</td>
                    <td className="px-8 py-5 border-b">{center.name}</td>
                    <td className="px-8 py-5 border-b">{center.parent?.name || '-'}</td>
                    <td className="px-8 py-5 border-b">{center.description || '-'}</td>
                    <td className="px-8 py-5 border-b">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        center.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {center.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-8 py-5 border-b">
                      <div className="flex space-x-2">
                        {hasPermission('erp_cost_center', 'write') && (
                          <button
                            onClick={() => {
                              setEditingCostCenter(center)
                              setFormData({
                                name: center.name,
                                code: center.code || '',
                                description: center.description || '',
                                parentId: center.parentId || '',
                                isActive: center.isActive,
                              })
                              setShowEditModal(true)
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                        )}
                        {hasPermission('erp_cost_center', 'delete') && (
                          <button
                            onClick={() => handleDelete(center.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Cost Center Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create Cost Center</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Marketing, Sales, Operations"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., MKT, SAL, OPS"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Description of this cost center..."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Cost Center</label>
                  <select
                    value={formData.parentId}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">None (Top Level)</option>
                    {costCenters
                      .filter(cc => cc.id !== editingCostCenter?.id && cc.isActive)
                      .map(cc => (
                        <option key={cc.id} value={cc.id}>{cc.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Cost Center
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Cost Center Modal */}
      {showEditModal && editingCostCenter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Edit Cost Center</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Marketing, Sales, Operations"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., MKT, SAL, OPS"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Description of this cost center..."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Cost Center</label>
                  <select
                    value={formData.parentId}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">None (Top Level)</option>
                    {costCenters
                      .filter(cc => cc.id !== editingCostCenter.id && cc.isActive)
                      .map(cc => (
                        <option key={cc.id} value={cc.id}>{cc.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingCostCenter(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Cost Center
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CostCenters
